import type { useCoreServices } from '@bangle.io/context';
import type { NoteContentStat } from '@bangle.io/service-core';
import {
  type FileTreeEntry,
  type FileTreeEntryAction,
  toast,
} from '@bangle.io/ui-components';
import { type WsFilePath, WsDirPath, WsPath } from '@bangle.io/ws-path';
import {
  Copy,
  CopyPlus,
  ExternalLink,
  FolderPlus,
  Lock,
  Move,
  Pencil,
  PlusIcon,
  Trash2,
} from 'lucide-react';
import { useMemo } from 'react';
import { toggleFolderPrivate, toggleNotePrivate } from '../share/note-privacy';
import {
  formatTreeEntryStatsLabel,
  wordsForTreeEntry,
} from './file-tree-entry-stats';

/**
 * Builds the context-menu actions for sidebar file-tree entries. Extracted
 * from AppSidebar so the sidebar component stays a thin composition layer;
 * every action dispatches a command and carries no state of its own.
 */
type CommandDispatcher = ReturnType<
  typeof useCoreServices
>['commandDispatcher'];

export function useSidebarFileActions({
  activeWsName,
  readOnly,
  commandDispatcher,
  cloudWorkspaceId,
  canManageNotePrivacy,
  markPathPrivate,
  notePaths,
  contentStats,
  wordsPerPage,
}: {
  activeWsName: string | undefined;
  readOnly: boolean;
  commandDispatcher: CommandDispatcher;
  cloudWorkspaceId: string | null;
  canManageNotePrivacy: boolean;
  /** Records a successful toggle so the row's lock badge updates at once. */
  markPathPrivate: (
    workspaceId: string,
    path: string,
    isPrivate: boolean,
  ) => void;
  notePaths: readonly WsFilePath[];
  contentStats: ReadonlyMap<string, NoteContentStat>;
  wordsPerPage: number;
}) {
  return useMemo(
    () =>
      (
        entry: FileTreeEntry,
        selectedEntries: readonly FileTreeEntry[],
      ): FileTreeEntryAction[] => {
        const actions: FileTreeEntryAction[] = [];
        const getFileWsPath = (relativePath: string) =>
          activeWsName
            ? WsPath.fromParts(activeWsName, relativePath).wsPath
            : undefined;
        const getDirWsPath = (relativePath: string) =>
          activeWsName
            ? WsDirPath.fromParts(activeWsName, relativePath).wsPath
            : undefined;
        const pushStatsAction = (noteWsPath?: string) => {
          const words = wordsForTreeEntry({
            kind: entry.kind,
            relativePath: entry.path,
            noteWsPath,
            notePaths,
            contentStats,
          });
          actions.push({
            id: 'entry-stats',
            label:
              words === undefined
                ? t.app.components.appSidebar.entryStatsUnavailable
                : formatTreeEntryStatsLabel(words, wordsPerPage),
            presentation: 'info',
            onClick: () => {},
          });
        };

        if (entry.kind === 'directory') {
          const dirWsPath = getDirWsPath(entry.path);
          pushStatsAction();

          if (!readOnly) {
            actions.push({
              id: 'new-note-here',
              label: t.app.components.appSidebar.newNoteHereActionTitle,
              Icon: PlusIcon,
              onClick: ({ entry }) => {
                commandDispatcher.dispatch(
                  'command::ws:quick-new-note',
                  {
                    pathPrefix: entry.path,
                  },
                  'ui',
                );
              },
            });

            actions.push({
              id: 'new-folder-here',
              label: t.app.components.appSidebar.newFolderHereActionTitle,
              Icon: FolderPlus,
              onClick: ({ entry }) => {
                commandDispatcher.dispatch(
                  'command::ui:create-directory-dialog',
                  {
                    pathPrefix: entry.path,
                  },
                  'ui',
                );
              },
            });
          }

          if (dirWsPath && !readOnly) {
            actions.push({
              id: 'rename-folder',
              label: t.app.components.appSidebar.renameActionTitle,
              Icon: Pencil,
              onClick: () => {
                commandDispatcher.dispatch(
                  'command::ui:rename-directory-dialog',
                  { dirWsPath },
                  'ui',
                );
              },
            });

            actions.push({
              id: 'duplicate-folder',
              label: t.app.components.appSidebar.duplicateActionTitle,
              Icon: CopyPlus,
              onClick: () => {
                commandDispatcher.dispatch(
                  'command::ws:clone-directory',
                  { dirWsPath },
                  'ui',
                );
              },
            });

            if (canManageNotePrivacy && cloudWorkspaceId) {
              actions.push({
                id: 'private-folder',
                label: t.app.components.appSidebar.privateFolderActionTitle,
                Icon: Lock,
                onClick: ({ entry }) => {
                  void toggleFolderPrivate(cloudWorkspaceId, entry.path)
                    .then((isPrivate) => {
                      markPathPrivate(cloudWorkspaceId, entry.path, isPrivate);
                      toast.success(
                        isPrivate
                          ? t.app.toasts.folderMarkedPrivate
                          : t.app.toasts.folderUnmarkedPrivate,
                      );
                    })
                    .catch(() => {
                      toast.error(t.app.toasts.notePrivateUpdateFailed);
                    });
                },
              });
            }

            actions.push({
              id: 'delete-folder',
              label: t.app.components.appSidebar.deleteActionTitle,
              Icon: Trash2,
              variant: 'destructive' as const,
              onClick: () => {
                commandDispatcher.dispatch(
                  'command::ui:delete-directory-dialog',
                  { dirWsPath },
                  'ui',
                );
              },
            });

            actions.push({
              id: 'move-folder-to-workspace',
              label:
                t.app.components.appSidebar.moveFolderToWorkspaceActionTitle,
              Icon: Move,
              onClick: () => {
                if (!dirWsPath) {
                  return;
                }
                commandDispatcher.dispatch(
                  'command::ui:move-notes-dialog',
                  { wsPaths: [], dirWsPath },
                  'ui',
                );
              },
            });
          }

          return actions;
        }

        const wsPath = getFileWsPath(entry.path);
        const filePath = wsPath ? WsPath.safeParseFile(wsPath).data : undefined;
        const selectedFileWsPaths = [
          ...new Set(
            selectedEntries
              .filter((selectedEntry) => selectedEntry.kind === 'file')
              .map((selectedEntry) => getFileWsPath(selectedEntry.path))
              .filter((selectedWsPath): selectedWsPath is string =>
                Boolean(selectedWsPath),
              ),
          ),
        ];
        const pushDeleteSelectedFilesAction = () => {
          actions.push({
            id: 'delete-selected-files',
            label: t.app.components.appSidebar.deleteSelectedFilesActionTitle({
              count: selectedFileWsPaths.length,
            }),
            Icon: Trash2,
            variant: 'destructive' as const,
            onClick: () => {
              commandDispatcher.dispatch(
                'command::ui:delete-files-dialog',
                { wsPaths: selectedFileWsPaths },
                'ui',
              );
            },
          });
        };
        const pushMoveSelectedFilesAction = () => {
          actions.push({
            id: 'move-selected-files',
            label: t.app.components.appSidebar.moveSelectedFilesActionTitle({
              count: selectedFileWsPaths.length,
            }),
            Icon: Move,
            onClick: () => {
              commandDispatcher.dispatch(
                'command::ui:move-notes-dialog',
                { wsPaths: selectedFileWsPaths },
                'ui',
              );
            },
          });
        };

        if (filePath) {
          if (filePath.isNote()) {
            pushStatsAction(wsPath);
          }
          actions.push({
            id: 'copy-path',
            label: t.app.components.appSidebar.copyPathActionTitle,
            Icon: Copy,
            onClick: ({ entry }) => {
              const wsPath = getFileWsPath(entry.path);
              if (!wsPath) {
                return;
              }
              commandDispatcher.dispatch(
                'command::ui:copy-workspace-path',
                { wsPath },
                'ui',
              );
            },
          });
        }

        if (filePath && !filePath.isNote()) {
          actions.push({
            id: 'open',
            label: t.app.components.appSidebar.openActionTitle,
            Icon: ExternalLink,
            onClick: ({ entry }) => {
              const wsPath = getFileWsPath(entry.path);
              if (!wsPath) {
                return;
              }
              commandDispatcher.dispatch(
                'command::ws:go-ws-path',
                { wsPath },
                'ui',
              );
            },
          });

          if (!readOnly) {
            actions.push({
              id: 'rename-file',
              label: t.app.components.appSidebar.renameActionTitle,
              Icon: Pencil,
              onClick: ({ entry }) => {
                const wsPath = getFileWsPath(entry.path);
                if (!wsPath) {
                  return;
                }
                commandDispatcher.dispatch(
                  'command::ui:rename-file-dialog',
                  { wsPath },
                  'ui',
                );
              },
            });
          }

          if (!readOnly && selectedFileWsPaths.length > 1) {
            pushMoveSelectedFilesAction();
            pushDeleteSelectedFilesAction();

            return actions;
          }

          if (!readOnly) {
            actions.push({
              id: 'delete-file',
              label: t.app.components.appSidebar.deleteActionTitle,
              Icon: Trash2,
              variant: 'destructive' as const,
              onClick: ({ entry }) => {
                const wsPath = getFileWsPath(entry.path);
                if (!wsPath) {
                  return;
                }
                commandDispatcher.dispatch(
                  'command::ui:delete-file-dialog',
                  { wsPath },
                  'ui',
                );
              },
            });
          }

          return actions;
        }

        if (filePath?.isNote()) {
          if (!readOnly && selectedFileWsPaths.length > 1) {
            pushMoveSelectedFilesAction();
            pushDeleteSelectedFilesAction();

            return actions;
          }

          if (!readOnly) {
            actions.push({
              id: 'rename',
              label: t.app.components.appSidebar.renameActionTitle,
              Icon: Pencil,
              onClick: ({ entry }) => {
                const wsPath = getFileWsPath(entry.path);
                if (!wsPath) {
                  return;
                }
                commandDispatcher.dispatch(
                  'command::ui:rename-note-dialog',
                  { wsPath },
                  'ui',
                );
              },
            });

            actions.push({
              id: 'move',
              label: t.app.components.appSidebar.moveActionTitle,
              Icon: Move,
              onClick: ({ entry }) => {
                const wsPath = getFileWsPath(entry.path);
                if (!wsPath) {
                  return;
                }
                commandDispatcher.dispatch(
                  'command::ui:move-note-dialog',
                  { wsPath },
                  'ui',
                );
              },
            });

            actions.push({
              id: 'duplicate',
              label: t.app.components.appSidebar.duplicateActionTitle,
              Icon: CopyPlus,
              onClick: ({ entry }) => {
                const wsPath = getFileWsPath(entry.path);
                if (!wsPath) {
                  return;
                }
                commandDispatcher.dispatch(
                  'command::ws:clone-note',
                  { wsPath },
                  'ui',
                );
              },
            });

            if (canManageNotePrivacy && cloudWorkspaceId) {
              actions.push({
                id: 'private-note',
                label: t.app.components.appSidebar.privateNoteActionTitle,
                Icon: Lock,
                onClick: ({ entry }) => {
                  const noteWsPath = getFileWsPath(entry.path);
                  const noteFile = noteWsPath
                    ? WsPath.safeParseFile(noteWsPath).data
                    : undefined;
                  if (!noteFile?.isNote()) {
                    return;
                  }
                  void toggleNotePrivate(cloudWorkspaceId, noteFile.path)
                    .then((isPrivate) => {
                      markPathPrivate(
                        cloudWorkspaceId,
                        noteFile.path,
                        isPrivate,
                      );
                      toast.success(
                        isPrivate
                          ? t.app.toasts.noteMarkedPrivate
                          : t.app.toasts.noteUnmarkedPrivate,
                      );
                    })
                    .catch(() => {
                      toast.error(t.app.toasts.notePrivateUpdateFailed);
                    });
                },
              });
            }
          }

          actions.push({
            id: 'delete',
            label: t.app.components.appSidebar.deleteActionTitle,
            Icon: Trash2,
            variant: 'destructive' as const,
            onClick: ({ entry }) => {
              const wsPath = getFileWsPath(entry.path);
              if (!wsPath) {
                return;
              }
              commandDispatcher.dispatch(
                'command::ui:delete-note-dialog',
                { wsPath },
                'ui',
              );
            },
          });
        }

        return actions;
      },
    [
      activeWsName,
      canManageNotePrivacy,
      cloudWorkspaceId,
      commandDispatcher,
      contentStats,
      markPathPrivate,
      notePaths,
      readOnly,
      wordsPerPage,
    ],
  );
}
